import { config } from '../config/environment';

class ApiService {
  constructor() {
    this.baseUrl = config.apiUrl;
    this.token = localStorage.getItem('acl_auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('acl_auth_token', token);
    }
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('acl_auth_token');
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('acl_auth_token');
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();

    const requiresAuth = !endpoint.startsWith('/auth/acl/send-verification') &&
      !endpoint.startsWith('/auth/verify-pin') &&
      !endpoint.startsWith('/auth/patient/verify-biometric') &&
      !endpoint.startsWith('/public/');

    if (requiresAuth && !token) {
      throw new Error('No authentication token found');
    }

    try {
      const headers = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      if (response.status === 401 || response.status === 403) {
        this.clearToken();
        window.location.href = '/login';
        throw new Error('Authentication failed');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[API] Request failed:', error);
      throw error;
    }
  }

  // ============ Public Endpoints (No Auth Required) ============

  async createAccountPublic(data) {
    // Public endpoint - no auth required
    // data: { first_name, last_name, email, phone, is_provider, proceed_with_partial_match }
    try {
      const response = await fetch(`${this.baseUrl}/public/create-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();
      
      // Handle different response scenarios
      // Success: { success: true, message: "Account created" }
      // Exact match: { success: false, match_type: "exact", message: "Account already exists" }
      // Partial match: { success: false, match_type: "partial", message: "Possible existing account" }
      return result;
    } catch (error) {
      console.error('[API] Create account failed:', error);
      throw error;
    }
  }

  // ============ Audit Logging ============

  /**
   * Create an audit log entry (requires authentication)
   * Used for: PATIENT_LIST_VIEWED, PATIENT_DETAIL_VIEWED
   */
  async createAuditLog(data) {
    return this.request('/erx_mobile_audit_logs', {
      method: 'POST',
      body: JSON.stringify({
        event_date: new Date().toISOString(),
        user_agent: navigator.userAgent,
        ...data
      })
    });
  }

  /**
   * Create an audit log entry (public, no auth required)
   * Used for: USER_LOGIN events before authentication
   */
  async createPublicAuditLog(data) {
    try {
      const response = await fetch(`${this.baseUrl}/public/audit-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_date: new Date().toISOString(),
          user_agent: navigator.userAgent,
          ...data
        })
      });
      return await response.json();
    } catch (error) {
      // Silently fail - audit logging should not block user actions
      console.error('[API] Public audit log failed:', error);
      return null;
    }
  }

  // ============ Auth Endpoints ============

  async sendVerificationCode(email) {
    return this.request('/auth/acl/send-verification', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async verifyPinCode(email, code, verificationId) {
    return this.request('/auth/verify-pin', {
      method: 'POST',
      body: JSON.stringify({
        email,
        code,
        verification_id: verificationId
      })
    });
  }

  async getMe() {
    return this.request('/auth/me', { method: 'GET' });
  }

  // ============ User Endpoints ============

  async updateUser(userId, data) {
    return this.request(`/user/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async updateAclCare(userId, aclCareData) {
    return this.request(`/user/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ acl_care: aclCareData })
    });
  }

  async getAllAllowedUsers(organizationId) {
    return this.request(`/acl/org-users?organization_id=${organizationId}`, {
      method: 'GET'
    });
  }

  async getAllUsers(organizationId) {
    return this.request(`/user?organization_id=${organizationId}`, {
      method: 'GET'
    });
  }

  async getAllACLUsers() {
    return this.request(`/acl/users`, {
      method: 'GET'
    });
  }

  // ============ Patient Endpoints ============

  async getPatient(patientId) {
    return this.request(`/patient/${patientId}`, { method: 'GET' });
  }

  async updatePatient(patientId, data) {
    return this.request(`/patient/${patientId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async createPatient(patientData) {
    return this.request('/patient', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  }

  // ============ Provider Endpoints ============

  async getOrganizationProviders(organizationId) {
    return this.request(`/organization/${organizationId}/providers`, { method: 'GET' });
  }

  async getProviderPatients(providerId) {
    return this.request(`/provider/${providerId}/patients`, { method: 'GET' });
  }

  // ============ ACL Training Sessions ============

  async createTrainingSession(data) {
    return this.request('/acl/training-sessions', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateTrainingSession(sessionId, data) {
    return this.request(`/acl/training-sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async getTrainingSessions(userId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/acl/training-sessions?user_id=${userId}${query ? '&' + query : ''}`, { method: 'GET' });
  }

  async removeUserFromOrganization(userId) {
    return this.request(`/user/${userId}`, {
      method: 'DELETE'
    });
  }

  // ============ ACL Test Outs ============

  async createTestOut(data) {
    return this.request('/acl/test-outs', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateTestOut(testOutId, data) {
    return this.request(`/acl/test-outs/${testOutId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async getTestOuts(userId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/acl/test-outs?user_id=${userId}${query ? '&' + query : ''}`, { method: 'GET' });
  }

  async getTestOutStats(organizationId) {
    return this.request(`/acl/test-out-stats?organization_id=${organizationId}`, { method: 'GET' });
  }

  // ============ ACL Onboarding ============

  async aclOnboardingComplete(userId) {
    return this.request('/acl/onboarding-complete', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    });
  }

  async getOnboardingStats(organizationId) {
    return this.request(`/acl/onboarding-stats?organization_id=${organizationId}`, { method: 'GET' });
  }

  // ============ ACL Access & Payment ============

  async validateAccessCode(code, userId) {
    return this.request('/acl/validate-access-code', {
      method: 'POST',
      body: JSON.stringify({ code, user_id: userId })
    });
  }

  async createCheckoutSession(userId, userEmail, discountCode = null) {
  return this.request('/acl/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ 
      user_id: userId,
      user_email: userEmail,
      discount_code: discountCode
    })
  });
}

  async startTrial(userId) {
    return this.request('/acl/start-trial', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    });
  }

  // ============ Support ============

  async createSupportRequest(data) {
    return this.request('/support_request', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // ============ User Management ============

  async addUser(userData) {
    return this.request('/add_vela_provider', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // ============ Unlock Codes ============

  async validateUnlockCode(code, phase) {
    return this.request('/acl/validate-unlock-code', {
      method: 'POST',
      body: JSON.stringify({ code, phase })
    });
  }
}

export const api = new ApiService();